import { Dayjs } from "dayjs";
import { TextField } from "@mui/material";
import { FieldValues, UseFormReturn } from "react-hook-form";

import { DateTimePickerField } from "./DateTimePickerField";
import { AutocompleteField } from "./AutoCompleteField";
import { ReactiveAutoCompleteField } from "./ReactiveAutoCompleteField";
import AttachmentBox from "./AttachmentBox";
import { FormFieldType, FormFieldTypesAllowed } from "../../types/form/form-types";
import { TimePickerField } from "./Timepicker";

interface DocumentFormFieldProps {
  field: FormFieldType;
  form: UseFormReturn<FieldValues, any>;
}
const FormField: React.FC<DocumentFormFieldProps> = ({ field, form }) => {
  const {
    register,
    setValue,
    formState: { errors },
  } = form;

  const error = field.id in errors;
  const error_message = errors?.[field.id]?.message ?? "";

  switch (field.type) {
    case FormFieldTypesAllowed.Textfield:
      return (
        <TextField
          label={field.label}
          size="small"
          placeholder={field.placeholder ?? field.label}
          disabled={Boolean(field.disabled)}
          {...register(field.id, { required: field.required })}
          error={error}
          helperText={error_message}
        />
      );
    case FormFieldTypesAllowed.Number:
      return (
        <TextField
          type="number"
          label={field.label}
          size="small"
          inputProps={{
            min: "0.00",
            step: "0.01",
            presicion: 2,
          }}
          placeholder={field.placeholder ?? field.label}
          disabled={Boolean(field.disabled)}
          {...register(field.id, { required: field.required })}
          error={error}
          helperText={error_message}
        />
      );
    case FormFieldTypesAllowed.Dropdown:
      return (
        <AutocompleteField
          id={field.id}
          options={field.options}
          label={field.label}
          onChange={(value: any) => setValue(field.id, value)}
          required={field.required}
          multiple={field.multiple}
          disabled={Boolean(field.disabled)}
          error={error}
          helperText={error_message.toString()}
          loading={field.loading}
          getOptionLabel={field.getOptionLabel}
          {...form}
        />
      );
    case FormFieldTypesAllowed.Attachment:
      return <AttachmentBox id={field.id} required={field.required} {...form} />;
    case FormFieldTypesAllowed.REACTIVE_DROPDOWN:
      return <ReactiveAutoCompleteField field={field} form={form} />;
    case FormFieldTypesAllowed.Date:
      return (
        <TextField
          type="date"
          label={field.label}
          size="small"
          placeholder={field.placeholder ?? field.label}
          disabled={Boolean(field.disabled)}
          {...register(field.id, { required: field.required })}
          error={error}
          helperText={error_message}
          InputLabelProps={{ shrink: true }}
        />
      );
    case FormFieldTypesAllowed.DateTime:
      return (
        <DateTimePickerField
          {...field}
          {...form}
          onChange={(value: Dayjs | null) => setValue(field.id, value)}
          error={error}
          helperText={error_message.toString()}
        />
      );
    case FormFieldTypesAllowed.Time:
      return (
        <TimePickerField
          {...field}
          {...form}
          onChange={(value: Dayjs | null) => setValue(field.id, value)}
          error={error}
          helperText={error_message.toString()}
          views={field.views}
        />
      );

    default:
      return <></>;
  }
};

export default FormField;
